// Translations of DATA names — the texts that come from the data, not from
// this widget's code — loaded from GET /api/translations (table
// i18n.translations; rule and workflow in docs/i18n.md). Interface texts are
// in de.ts / en.ts.
//
// Keys are the German texts as the data carries them (WZ industries: their
// code). Anything without a translation shows in German, never blank. German
// needs nothing loaded: dataLabel() returns the source unchanged.
import { getLang, type Lang } from './lang';

export type DataDomain = 'investments.industry' | 'investments.spotlight' | 'common.federal_state';

type TranslationMap = Partial<Record<DataDomain, Record<string, string>>>;

const SCOPE = 'investments';

let translations: TranslationMap = {};
let loaded = false;
let pending: Promise<void> | null = null;

/** Fetches this widget's translations once per page; concurrent and later
    calls share the request. Never rejects: on failure the widget keeps the
    German names, and the next call (e.g. a later language switch) retries. */
export function loadDataTranslations(apiBase: string): Promise<void> {
  if (loaded) return Promise.resolve();
  if (!pending) {
    pending = fetch(`${apiBase}/translations?lang=en&scope=${SCOPE}`)
      .then(res => (res.ok ? (res.json() as Promise<TranslationMap>) : Promise.reject(new Error(String(res.status)))))
      .then(map => {
        translations = map;
        loaded = true;
      })
      .catch(err => {
        console.warn('[investments-widget] data translations unavailable, showing German names', err);
        pending = null;
      });
  }
  return pending;
}

/**
 * Calls show() when the widget can render in `lang`: at once in German or once
 * the translations are loaded; in English after they arrive. If they take
 * longer than timeoutMs, show() runs with German names and once more when they
 * land. Returns a cancel function (a newer language switch supersedes it).
 */
export function whenDataReady(lang: Lang, apiBase: string, show: () => void, timeoutMs = 1500): () => void {
  if (lang !== 'en' || loaded) {
    show();
    return () => {};
  }
  let cancelled = false;
  let shown = false;
  const timer = setTimeout(() => {
    if (cancelled) return;
    shown = true;
    show();
  }, timeoutMs);
  void loadDataTranslations(apiBase).then(() => {
    clearTimeout(timer);
    if (cancelled) return;
    // First render, or a refresh now that the names are here. After a failed
    // load that already rendered German, there is nothing new to show.
    if (!shown || loaded) show();
  });
  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}

/** True once the translations have arrived (render() mounts English after that). */
export function hasDataTranslations(): boolean {
  return loaded;
}

/** A data name in the widget language: the translation, or the German source. */
export function dataLabel(domain: DataDomain, source: string): string {
  if (getLang() !== 'en') return source;
  return translations[domain]?.[source] ?? source;
}

/** Test hook: install a translation table without a request. */
export function setDataTranslations(map: TranslationMap): void {
  translations = map;
  loaded = true;
}
